/**
 * captureDispatcher.js — lifecycle manager for chat capture evaluate calls.
 *
 * Closes three D.4 plan-review gaps:
 *
 *   1. Stale-response binding — a monotonic sequence counter discards results
 *      whose in-flight slot was replaced or evicted. No result ever binds to
 *      the wrong chat bubble.
 *
 *   2. Ghost callbacks after Home unmount — destroy() marks every in-flight
 *      entry destroyed=true; resolve handlers early-return. Prevents late-
 *      arriving toasts / setState calls on a torn-down component.
 *
 *   3. 3-in-flight cap via oldest-wins eviction. Evicted evaluates still hit
 *      the network (evaluate is idempotent + Rule 104 requires a candidate
 *      row for audit) — their results are silently discarded.
 *
 * Intentional design choices:
 *
 *   - dispatch() is a plain synchronous function returning void. Rule 108's
 *     hot-path discipline — evaluate must NEVER block /api/ai-assistant's
 *     latency — becomes syntactically unviolatable (no Promise to await).
 *
 *   - NO AbortController. Evaluate is idempotent; a wasted Haiku call on
 *     eviction costs a fraction of a cent and still writes a useful audit
 *     row. Simpler than threading abort signals through chatCapture.js.
 *
 * Caller contract:
 *
 *   dispatch(messageId, rawText, chatMessageId, onResult, opts?)
 *     opts: { assistantResponse, conversationId } — both optional, both
 *       forwarded to /api/chat/capture/evaluate. Used by the chat-logging
 *       export so the candidate row stores the full Q&A pair and a
 *       session-scoped thread id. Omitting either keeps the legacy shape
 *       (column stays NULL on the candidate row).
 *     onResult(result, chatMessageId) — fires on success with the evaluate
 *       response body. On fetch rejection fires with { error: true, message }.
 *     Callback MAY fire after the component that dispatched has unmounted
 *     (guarded by the destroyed flag in the normal case) — so callers MUST
 *     use functional setState updater form to avoid stale closures on state.
 *
 *   cancel(messageId)  — mark an in-flight entry no-longer-relevant.
 *   destroy()          — mark every in-flight entry destroyed; clear the map.
 *
 * State shape:
 *
 *   inFlight: Map<messageId, { seq, destroyed }>
 *
 *   On resolve/reject, the entry is looked up by messageId. The callback
 *   fires only if entry.seq === the seq captured at dispatch time AND
 *   entry.destroyed === false. Otherwise the result is silently discarded.
 */

import { evaluateMessage } from '../api/chatCapture';


const MAX_IN_FLIGHT = 3;


export function createDispatcher() {
  let sequenceCounter = 0;
  const inFlight = new Map();  // messageId → { seq, destroyed }

  function evictOldestIfAtCap() {
    if (inFlight.size < MAX_IN_FLIGHT) return;
    let oldestId = null;
    let oldestSeq = Infinity;
    for (const [id, entry] of inFlight.entries()) {
      if (entry.seq < oldestSeq) {
        oldestSeq = entry.seq;
        oldestId = id;
      }
    }
    if (oldestId === null) return;
    const oldest = inFlight.get(oldestId);
    if (oldest) oldest.destroyed = true;
    inFlight.delete(oldestId);
    console.log('[CHAT-CAPTURE] evicted for cap',
      { evictedMessageId: oldestId, evictedSeq: oldestSeq });
  }

  function dispatch(messageId, rawText, chatMessageId, onResult, opts = {}) {
    evictOldestIfAtCap();

    sequenceCounter += 1;
    const seq = sequenceCounter;
    const entry = { seq, destroyed: false };
    inFlight.set(messageId, entry);
    console.log('[CHAT-CAPTURE] dispatch', { messageId, seq });

    function deliver(payload) {
      const current = inFlight.get(messageId);
      if (!current || current.seq !== seq || current.destroyed) {
        console.log('[CHAT-CAPTURE] discarded stale result',
          { messageId, resultSeq: seq, currentSeq: current?.seq ?? null });
        return;
      }
      inFlight.delete(messageId);
      try {
        onResult(payload, chatMessageId);
      } catch (cbErr) {
        console.log('[CHAT-CAPTURE] onResult threw', cbErr);
      }
    }

    evaluateMessage(rawText, chatMessageId, opts).then(
      (result) => deliver(result),
      (err) => deliver({ error: true, message: err?.message || String(err) })
    );
  }

  function cancel(messageId) {
    const entry = inFlight.get(messageId);
    if (entry) {
      entry.destroyed = true;
      inFlight.delete(messageId);
    }
  }

  function destroy() {
    for (const entry of inFlight.values()) entry.destroyed = true;
    inFlight.clear();
  }

  return { dispatch, cancel, destroy };
}
